// client/src/hooks/useCollaborationSignals.ts
// Hook for subscribing to and emitting collaboration signals

import { useEffect, useCallback, useState, useRef } from 'react';
import { realtimeClient } from '../realtime/socketClient';
import {
  RealtimeEventType,
  CollaborationSignalPayload,
  CollaborationSignalType,
} from '../realtime/events';
import { useEmitCollaborationSignalMutation } from '../api/threadCollaborationApi';

interface ActiveSignal {
  userId: string;
  userFullName: string;
  signalType: CollaborationSignalType;
  expiresAt?: Date;
}

interface UseCollaborationSignalsOptions {
  workspaceId: string;
  threadId: string;
  userId?: string;
  enabled?: boolean;
}

/**
 * Hook to manage collaboration signals for a thread.
 * Handles subscribing to signals from other users and emitting own signals.
 */
// Throttle interval for typing signals (max 1 per 2 seconds)
const TYPING_THROTTLE_MS = 2000;
// Auto-stop typing after this duration of inactivity
const TYPING_IDLE_TIMEOUT_MS = 1500;

export function useCollaborationSignals({
  workspaceId,
  threadId,
  userId,
  enabled = true,
}: UseCollaborationSignalsOptions) {
  const [activeSignals, setActiveSignals] = useState<Map<string, ActiveSignal>>(
    new Map()
  );
  const [emitSignal] = useEmitCollaborationSignalMutation();
  const cleanupIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTypingEmitRef = useRef<number>(0);
  const isTypingRef = useRef<boolean>(false);

  // Handle incoming collaboration signal
  const handleCollaborationSignal = useCallback(
    (payload: CollaborationSignalPayload) => {
      // Only process signals for this thread
      if (payload.threadId !== threadId) return;
      // Ignore own signals
      if (payload.userId === userId) return;

      setActiveSignals((prev) => {
        const next = new Map(prev);

        if (payload.signalType === 'idle') {
          // Remove signal when idle
          next.delete(payload.userId);
        } else {
          // Add or update signal
          next.set(payload.userId, {
            userId: payload.userId,
            userFullName: payload.userFullName,
            signalType: payload.signalType,
            expiresAt: payload.expiresAt ? new Date(payload.expiresAt) : undefined,
          });
        }

        return next;
      });
    },
    [threadId, userId]
  );

  // Subscribe to collaboration signals
  useEffect(() => {
    if (!enabled || !workspaceId || !threadId) return;

    const topic = `workspace:${workspaceId}`;

    // Subscribe to workspace
    realtimeClient.subscribe(topic);

    // Set up event listener
    const unsubscribe = realtimeClient.on<CollaborationSignalPayload>(
      topic,
      handleCollaborationSignal,
      RealtimeEventType.COLLABORATION_SIGNAL
    );

    // Clean up expired signals every 5 seconds
    cleanupIntervalRef.current = setInterval(() => {
      const now = new Date();
      setActiveSignals((prev) => {
        const next = new Map(prev);
        let changed = false;

        for (const [key, signal] of next) {
          if (signal.expiresAt && signal.expiresAt < now) {
            next.delete(key);
            changed = true;
          }
        }

        return changed ? next : prev;
      });
    }, 5000);

    return () => {
      unsubscribe();
      realtimeClient.unsubscribe(topic);
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
      }
    };
  }, [enabled, workspaceId, threadId, handleCollaborationSignal]);

  // Emit a signal
  const sendSignal = useCallback(
    async (signalType: CollaborationSignalType, metadata?: Record<string, unknown>) => {
      if (!enabled || !workspaceId || !threadId) return;

      try {
        await emitSignal({
          threadId,
          workspaceId,
          signalType,
          metadata,
        });
      } catch (error) {
        console.error('Failed to emit collaboration signal:', error);
      }
    },
    [enabled, workspaceId, threadId, emitSignal]
  );

  // Start typing indicator with throttle (max 1 emit per TYPING_THROTTLE_MS)
  const startTyping = useCallback(() => {
    // Clear existing idle timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    const now = Date.now();
    const timeSinceLastEmit = now - lastTypingEmitRef.current;

    // Only emit if we haven't emitted recently (throttle)
    if (timeSinceLastEmit >= TYPING_THROTTLE_MS) {
      sendSignal('typing');
      lastTypingEmitRef.current = now;
      isTypingRef.current = true;
    }

    // Auto-stop typing after TYPING_IDLE_TIMEOUT_MS of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      if (isTypingRef.current) {
        sendSignal('idle');
        isTypingRef.current = false;
      }
    }, TYPING_IDLE_TIMEOUT_MS);
  }, [sendSignal]);

  // Stop typing indicator
  const stopTyping = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (isTypingRef.current) {
      sendSignal('idle');
      isTypingRef.current = false;
    }
  }, [sendSignal]);

  // Clean up typing timeout on unmount and emit stopTyping on page leave
  useEffect(() => {
    // Handle page visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.hidden && isTypingRef.current) {
        sendSignal('idle');
        isTypingRef.current = false;
      }
    };

    // Handle page unload/close
    const handleBeforeUnload = () => {
      if (isTypingRef.current) {
        // Use sendBeacon for reliable delivery on page close
        const url = `/api/threads/${threadId}/signals?workspaceId=${workspaceId}`;
        navigator.sendBeacon(url, JSON.stringify({ signalType: 'idle' }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Emit idle on unmount if still typing
      if (isTypingRef.current) {
        sendSignal('idle');
        isTypingRef.current = false;
      }
    };
  }, [threadId, workspaceId, sendSignal]);

  // Get signals as array for display
  const signalsArray = Array.from(activeSignals.values());

  // Get typing users
  const typingUsers = signalsArray.filter((s) => s.signalType === 'typing');

  // Get users on call
  const usersOnCall = signalsArray.filter((s) => s.signalType === 'on_call');

  // Get users listening to voicemail
  const usersListeningVoicemail = signalsArray.filter(
    (s) => s.signalType === 'listening_voicemail'
  );

  return {
    activeSignals: signalsArray,
    typingUsers,
    usersOnCall,
    usersListeningVoicemail,
    sendSignal,
    startTyping,
    stopTyping,
  };
}

export default useCollaborationSignals;
